import { useCallback, useEffect, useState } from 'react';
/**
 * Measures the rendered width of each frozen column and returns its `left` offset in px.
 *
 * Offsets are measured, not derived from each column's declared `width`, because the two do not
 * agree: padding and the table's own box-sizing mean a column declared `64px` can render at 89px,
 * and the selection column has no declared width at all. Using declared widths made every frozen
 * column after the first stick a little to the left of where it actually sits, so the columns slid
 * sideways before locking (measured at 28px and 53px of drift on the attachments table).
 *
 * Re-measures on resize, which is when the drift was most visible — column widths are fluid, so a
 * narrower viewport changes every offset. It observes the frozen header cells themselves, not only
 * the table: columns can redistribute (a column toggled, reordered, or its content reflowing) while
 * the table's own width never changes, and observing only the table would leave stale offsets on
 * the new layout.
 *
 * @param tableRef the `<table>` element
 * @param pinnedCellCount total frozen leading cells, INCLUDING the selection column when present
 * @param enabled skip all work (and all listeners) when nothing is pinned
 */
export function usePinnedColumnOffsets(tableRef, pinnedCellCount, enabled) {
    const [offsets, setOffsets] = useState([]);
    const measure = useCallback(() => {
        const table = tableRef.current;
        if (!table || !enabled || pinnedCellCount <= 0)
            return;
        const headerCells = table.querySelectorAll('thead tr:first-child > th');
        const next = [];
        let accumulated = 0;
        for (let i = 0; i < pinnedCellCount; i += 1) {
            next.push(accumulated);
            const cell = headerCells[i];
            if (!cell)
                break;
            accumulated += cell.getBoundingClientRect().width;
        }
        setOffsets((prev) => prev.length === next.length && prev.every((v, i) => Math.abs(v - next[i]) < 0.5) ? prev : next);
    }, [tableRef, pinnedCellCount, enabled]);
    useEffect(() => {
        if (!enabled)
            return undefined;
        measure();
        const table = tableRef.current;
        if (!table || typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        // The table catches viewport-driven resizes; each frozen header cell catches the case the
        // table cannot see — columns redistributing while the table's own width stays put.
        observer.observe(table);
        table
            .querySelectorAll('thead tr:first-child > th')
            .forEach((cell, i) => {
            if (i < pinnedCellCount)
                observer.observe(cell);
        });
        return () => observer.disconnect();
    }, [measure, enabled, tableRef, pinnedCellCount]);
    return offsets;
}
